/**
 * layoutModel — pure helpers for reading/updating the per-user Webtop layout
 * object (the thing persisted by desktopLayoutSlice / useDesktopLayoutSync).
 *
 * Every mutator returns a NEW layout object (immutability) so callers can pass
 * the result straight to updateLayout()/replaceLayout(). Keeping this pure makes
 * it fully unit-testable.
 *
 * An icon's stable key is its top-level section name.
 */

export const THEMES = ["light", "dark"];

export const ACCENTS = ["#0067c0", "#8b5cf6", "#0e7490", "#c2410c", "#16a34a", "#db2777"];

// Wallpaper id → CSS background value.
export const WALLPAPERS = {
  "bloom-blue":
    "radial-gradient(120% 120% at 20% 10%, #2b5bb5 0%, #1f3c88 35%, #14245a 70%, #0d183f 100%)",
  "sunset":
    "radial-gradient(120% 120% at 80% 0%, #b45309 0%, #7c2d12 40%, #3b0764 80%, #1e1b4b 100%)",
  "forest":
    "radial-gradient(120% 120% at 30% 0%, #15803d 0%, #064e3b 45%, #0f172a 85%)",
  "graphite":
    "linear-gradient(160deg, #334155 0%, #1e293b 50%, #0f172a 100%)",
  "rose":
    "radial-gradient(120% 120% at 70% 10%, #be185d 0%, #831843 40%, #3b0764 80%, #1e1b4b 100%)",
};

export const DEFAULT_WALLPAPER = "bloom-blue";

export const DEFAULT_LAYOUT = {
  iconPositions: {},
  hiddenIcons: [],
  taskbarPins: [],
  wallpaper: DEFAULT_WALLPAPER,
  theme: "light",
  accent: "#0067c0",
};

const L = (layout) => layout || {};

// --- icon positions ---
export const getIconPosition = (layout, key) => L(layout).iconPositions?.[key] || null;

export const setIconPosition = (layout, key, pos) => ({
  ...L(layout),
  iconPositions: { ...(L(layout).iconPositions || {}), [key]: { x: pos.x, y: pos.y } },
});

// --- hidden (removed-from-desktop) icons ---
export const isHidden = (layout, key) => (L(layout).hiddenIcons || []).includes(key);

export const hideIcon = (layout, key) => ({
  ...L(layout),
  hiddenIcons: Array.from(new Set([...(L(layout).hiddenIcons || []), key])),
});

export const showIcon = (layout, key) => ({
  ...L(layout),
  hiddenIcons: (L(layout).hiddenIcons || []).filter((k) => k !== key),
});

export const restoreAllIcons = (layout) => ({ ...L(layout), hiddenIcons: [] });

// --- taskbar pins ---
export const isPinned = (layout, key) => (L(layout).taskbarPins || []).includes(key);

export const pinApp = (layout, key) => ({
  ...L(layout),
  taskbarPins: Array.from(new Set([...(L(layout).taskbarPins || []), key])),
});

export const unpinApp = (layout, key) => ({
  ...L(layout),
  taskbarPins: (L(layout).taskbarPins || []).filter((k) => k !== key),
});

export const togglePin = (layout, key) =>
  isPinned(layout, key) ? unpinApp(layout, key) : pinApp(layout, key);

// --- appearance ---
export const getTheme = (layout) => L(layout).theme || DEFAULT_LAYOUT.theme;
export const getAccent = (layout) => L(layout).accent || DEFAULT_LAYOUT.accent;
export const getWallpaperId = (layout) => L(layout).wallpaper || DEFAULT_WALLPAPER;
export const getWallpaperCss = (layout) =>
  WALLPAPERS[getWallpaperId(layout)] || WALLPAPERS[DEFAULT_WALLPAPER];

export const setTheme = (layout, theme) => ({ ...L(layout), theme });
export const setAccent = (layout, accent) => ({ ...L(layout), accent });
export const setWallpaper = (layout, wallpaper) => ({ ...L(layout), wallpaper });

// Default grid position for an icon by its index (column-major), used when the
// icon has no saved position yet.
export const ICON_W = 92;
export const ICON_H = 96;
const GAP = 4;
const ORIGIN_X = 16;
const ORIGIN_Y = 16;
const ROWS_PER_COL = 5;

export const defaultPositionForIndex = (index) => {
  const col = Math.floor(index / ROWS_PER_COL);
  const row = index % ROWS_PER_COL;
  return {
    x: ORIGIN_X + col * (ICON_W + GAP),
    y: ORIGIN_Y + row * (ICON_H + GAP),
  };
};

// --- record shortcuts (pin a specific lead/request/client to the desktop) ---
// A shortcut is { id, path, label, type, x, y } where `path` is the deep link
// (e.g. "/sales/123") and `type` is its first segment.
export const getShortcuts = (layout) => L(layout).recordShortcuts || [];

export const hasShortcutForPath = (layout, path) =>
  getShortcuts(layout).some((s) => s.path === path);

export const addShortcut = (layout, shortcut) => {
  const list = getShortcuts(layout);
  if (list.some((s) => s.path === shortcut.path)) return L(layout); // already pinned
  return { ...L(layout), recordShortcuts: [...list, shortcut] };
};

export const removeShortcut = (layout, id) => ({
  ...L(layout),
  recordShortcuts: getShortcuts(layout).filter((s) => s.id !== id),
});

export const removeShortcutByPath = (layout, path) => ({
  ...L(layout),
  recordShortcuts: getShortcuts(layout).filter((s) => s.path !== path),
});

export const setShortcutPosition = (layout, id, pos) => ({
  ...L(layout),
  recordShortcuts: getShortcuts(layout).map((s) =>
    s.id === id ? { ...s, x: pos.x, y: pos.y } : s
  ),
});

// Starting position for a new shortcut — a column to the right of app icons.
export const nextShortcutPosition = (layout) =>
  defaultPositionForIndex(40 + getShortcuts(layout).length);

// --- sticky notes ---
// A note is { id, text, x, y, w, h, color }.
export const STICKY_COLORS = ["#fde68a", "#bbf7d0", "#fbcfe8", "#bfdbfe", "#fed7aa"];
export const NOTE_MIN_W = 140;
export const NOTE_MIN_H = 120;

export const getNotes = (layout) => L(layout).stickyNotes || [];

// Notes shown ON the desktop. A note with open === false is "closed" — it stays
// stored (visible in the Sticky Notes app) but is hidden from the desktop.
// Legacy notes (no `open` field) are treated as open. (Windows Sticky Notes model.)
export const getOpenNotes = (layout) => getNotes(layout).filter((n) => n.open !== false);

export const makeNote = (id, x, y) => ({
  id,
  text: "",
  x: Math.max(0, x),
  y: Math.max(0, y),
  w: 200,
  h: 170,
  color: STICKY_COLORS[0],
  open: true,
});

export const addNote = (layout, note) => ({
  ...L(layout),
  stickyNotes: [...getNotes(layout), note],
});

export const updateNote = (layout, id, patch) => ({
  ...L(layout),
  stickyNotes: getNotes(layout).map((n) => (n.id === id ? { ...n, ...patch } : n)),
});

export const removeNote = (layout, id) => ({
  ...L(layout),
  stickyNotes: getNotes(layout).filter((n) => n.id !== id),
});

// ── Notepad documents ──────────────────────────────────────────────────────
// Persisted text documents for the Notepad app (per-user, backend autosave).
export const getNotepadDocs = (layout) => L(layout).notepadDocs || [];

export const makeDoc = (id, title) => ({
  id,
  title: title || "Untitled",
  content: "",
});

export const addDoc = (layout, doc) => ({
  ...L(layout),
  notepadDocs: [...getNotepadDocs(layout), doc],
});

export const updateDoc = (layout, id, patch) => ({
  ...L(layout),
  notepadDocs: getNotepadDocs(layout).map((d) => (d.id === id ? { ...d, ...patch } : d)),
});

export const removeDoc = (layout, id) => ({
  ...L(layout),
  notepadDocs: getNotepadDocs(layout).filter((d) => d.id !== id),
});

// Reset to a fresh default layout (keeps nothing user-specific).
export const resetLayoutObject = () => ({
  ...DEFAULT_LAYOUT,
  iconPositions: {},
  hiddenIcons: [],
  taskbarPins: [],
  recordShortcuts: [],
  stickyNotes: [],
  notepadDocs: [],
});
